import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import {
  MapPin,
  Download,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  Globe,
  Phone,
  Mail,
  Star,
  ArrowLeft,
  Play,
  Crosshair,
  Building2,
  User,
  Linkedin,
  AtSign,
  ExternalLink,
  Navigation
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GeoSearchPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [address, setAddress] = useState('');
  const [radiusKm, setRadiusKm] = useState(10);
  const [businessType, setBusinessType] = useState('');
  const [maxResultsPerComune, setMaxResultsPerComune] = useState(5);
  
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  // Esempi di tipi di business
  const businessExamples = [
    "Dentisti", "Commercialisti", "Avvocati", "Architetti",
    "Idraulici", "Elettricisti", "Ristoranti", "Palestre",
    "Parrucchieri", "Officine meccaniche", "Farmacie", "Veterinari"
  ];

  // Polling per lo stato del job
  useEffect(() => {
    let interval;
    if (currentJob && polling) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/scraper/status/${currentJob}`);
          setJobStatus(response.data);
          
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            setPolling(false);
            if (response.data.status === 'completed') {
              toast.success(`Ricerca completata! ${response.data.progress} aziende trovate`);
            } else {
              toast.error(`Errore: ${response.data.error}`);
            }
          }
        } catch (error) {
          console.error('Error polling status:', error);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [currentJob, polling]);

  const handlePreview = async () => {
    if (!address.trim() || !businessType.trim()) {
      toast.error('Inserisci indirizzo e tipo di attività');
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await axios.post(`${API_URL}/geo/preview`, {
        address: address.trim(),
        radius_km: radiusKm,
        business_type: businessType.trim(),
        max_results_per_comune: maxResultsPerComune
      });
      setPreview(response.data);
      toast.success(`Trovati ${response.data.totale_comuni} comuni nel raggio`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella ricerca');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStartSearch = async () => {
    if (!preview) {
      toast.error('Prima visualizza l\'anteprima');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/geo/start`, {
        address: address.trim(),
        radius_km: radiusKm,
        business_type: businessType.trim(),
        max_results_per_comune: maxResultsPerComune
      });
      
      setCurrentJob(response.data.job_id);
      setJobStatus({ 
        status: 'pending', 
        results: [],
        comuni_completati: 0,
        totale_comuni: preview.totale_comuni
      });
      setPolling(true);
      toast.info(`Ricerca avviata in ${preview.totale_comuni} comuni`);
    } catch (error) {
      toast.error('Errore nell\'avvio della ricerca');
    }
  };

  const exportCSV = async () => {
    if (!currentJob) return;
    try {
      const response = await axios.get(`${API_URL}/scraper/results/${currentJob}/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `geo_search_${radiusKm}km_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV scaricato!');
    } catch (error) {
      toast.error('Errore nel download del CSV');
    }
  };

  const saveToLeads = async () => {
    if (!currentJob) return;
    try {
      const response = await axios.post(`${API_URL}/scraper/save-to-leads/${currentJob}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'In attesa...';
      case 'running': return `Scraping comune: ${jobStatus?.current_comune || ''}`;
      case 'enriching_web': return 'Estrazione email e LinkedIn...';
      case 'completed': return 'Completato';
      case 'failed': return 'Fallito';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
                <Crosshair className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Ricerca per Raggio</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pannello Configurazione */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Navigation className="w-5 h-5" />
                    Configura Ricerca
                  </CardTitle>
                  <CardDescription>
                    Trova aziende nel raggio dal tuo ufficio
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Indirizzo del tuo ufficio</Label>
                    <Input
                      placeholder="es. Via Roma 1, Bergamo"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      data-testid="geo-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Raggio di ricerca</Label>
                    <Select value={String(radiusKm)} onValueChange={(v) => setRadiusKm(Number(v))}>
                      <SelectTrigger data-testid="geo-radius-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 km</SelectItem>
                        <SelectItem value="10">10 km</SelectItem>
                        <SelectItem value="15">15 km</SelectItem>
                        <SelectItem value="20">20 km</SelectItem>
                        <SelectItem value="30">30 km</SelectItem>
                        <SelectItem value="50">50 km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo di attività</Label>
                    <Input
                      placeholder="es. Dentisti, Commercialisti..."
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      data-testid="geo-business-input"
                    />
                    <div className="flex flex-wrap gap-1 mt-2">
                      {businessExamples.slice(0, 6).map((example, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline"
                          className="cursor-pointer hover:bg-slate-100 text-xs"
                          onClick={() => setBusinessType(example)}
                        >
                          {example}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Risultati per comune</Label>
                    <Select value={String(maxResultsPerComune)} onValueChange={(v) => setMaxResultsPerComune(Number(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 per comune</SelectItem>
                        <SelectItem value="5">5 per comune</SelectItem>
                        <SelectItem value="10">10 per comune</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewLoading || !address.trim() || !businessType.trim()}
                    data-testid="geo-preview-btn"
                  >
                    {previewLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calcolo...</>
                    ) : (
                      <><MapPin className="w-4 h-4 mr-2" /> Anteprima Comuni</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Anteprima Comuni */}
            {preview && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-emerald-800">
                      {preview.totale_comuni} Comuni nel Raggio
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {preview.comuni_trovati.map((comune, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{comune.nome}</span>
                          <Badge variant="secondary" className="text-xs">
                            {comune.distanza_km} km
                          </Badge>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-emerald-200">
                      <p className="text-sm text-emerald-700 mb-3">
                        Tempo stimato: ~{preview.stima_tempo_minuti} minuti
                      </p>
                      <Button 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={handleStartSearch}
                        disabled={polling}
                        data-testid="geo-start-btn"
                      >
                        {polling ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ricerca in corso...</>
                        ) : (
                          <><Play className="w-4 h-4 mr-2" /> Avvia Ricerca</>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Pannello Risultati */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-slate-200 min-h-[600px]">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Risultati</CardTitle>
                      {jobStatus && (
                        <CardDescription className="flex items-center gap-2 mt-1">
                          {jobStatus.status === 'completed' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : jobStatus.status === 'failed' ? (
                            <XCircle className="w-4 h-4 text-rose-500" />
                          ) : (
                            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                          )}
                          {getStatusLabel(jobStatus.status)}
                        </CardDescription>
                      )}
                    </div>
                    
                    {jobStatus?.status === 'completed' && jobStatus?.results?.length > 0 && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={exportCSV}>
                          <Download className="w-4 h-4 mr-2" />
                          Export CSV
                        </Button>
                        <Button size="sm" onClick={saveToLeads}>
                          <Save className="w-4 h-4 mr-2" />
                          Salva Lead
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Progress */}
                  {polling && jobStatus && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-slate-500 mb-1">
                        <span>Comuni completati: {jobStatus.comuni_completati || 0}/{jobStatus.totale_comuni || 0}</span>
                        <span>{jobStatus.progress || 0} aziende trovate</span>
                      </div>
                      <Progress 
                        value={jobStatus.totale_comuni ? (jobStatus.comuni_completati / jobStatus.totale_comuni) * 100 : 0} 
                        className="h-2"
                      />
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="p-0">
                  {!jobStatus ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                        <Crosshair className="w-10 h-10 text-emerald-600" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">
                        Ricerca Geografica
                      </h3>
                      <p className="text-slate-500 text-center max-w-md">
                        Inserisci l'indirizzo del tuo ufficio, scegli il raggio e il tipo 
                        di attività per trovare tutte le aziende nella zona
                      </p>
                    </div>
                  ) : jobStatus.results?.length === 0 && jobStatus.status !== 'completed' ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
                      <p className="text-slate-600">{getStatusLabel(jobStatus.status)}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        Stiamo cercando in tutti i comuni del raggio...
                      </p>
                    </div>
                  ) : jobStatus.results?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <XCircle className="w-12 h-12 text-slate-400 mb-4" />
                      <p className="text-slate-600">Nessun risultato trovato</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                      {jobStatus.results.map((result, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 hover:bg-slate-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-medium text-slate-900">{result.name}</h3>
                                {result.rating && (
                                  <Badge variant="outline" className="text-xs">
                                    <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
                                    {result.rating}
                                  </Badge>
                                )}
                                {result.distanza_km !== undefined && (
                                  <Badge variant="secondary" className="text-xs">
                                    {result.distanza_km} km
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
                                {result.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {result.phone}
                                  </span>
                                )}
                                {result.comune_ricerca && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {result.comune_ricerca}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                                {result.website && (
                                  <a 
                                    href={result.website.startsWith('http') ? result.website : `https://${result.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                  >
                                    <Globe className="w-3 h-3" />
                                    Sito
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                                {(result.enrichment?.emails_found?.length > 0 || result.email) && (
                                  <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                    <Mail className="w-3 h-3" />
                                    {result.email || result.enrichment?.emails_found?.[0]}
                                  </span>
                                )}
                              </div>
                              
                              {/* LinkedIn */}
                              {result.enrichment && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {(result.enrichment.linkedin_search_url || result.enrichment.linkedin_company_url) && (
                                    <a 
                                      href={result.enrichment.linkedin_company_url || result.enrichment.linkedin_search_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
                                    >
                                      <Linkedin className="w-3 h-3" />
                                      LinkedIn
                                    </a>
                                  )}
                                  {result.enrichment.pec_probable && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs">
                                      <AtSign className="w-3 h-3" />
                                      {result.enrichment.pec_probable}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GeoSearchPage;
